// Holding two birds against each other: what the pair can be compared ON, and
// which birds are worth comparing a given one WITH.
//
// A field guide's real work is done on facing pages — Willow Flycatcher on the
// left, Alder on the right, and the difference between them is the only thing
// that ever teaches either. Every screen in this app until now has shown one
// bird at a time, so the comparison had to be done from memory across a
// navigation, which is exactly the thing a learner cannot yet do.
//
// This module is the pure half of that view: no DOM, no media, no region
// singleton, so the node test suite can reach it. Everything here answers one
// of two questions — "what should the two panes be showing?" and "who else
// should this dropdown offer?".

import { t } from '../strings';
import { confusablesFor, type Species, type SoundType } from '../types';

/**
 * One thing the two panes show at the same time.
 *
 * Deliberately flat rather than `{ kind: 'sound', type }`: a facet is a tab on
 * a bar, a segment of a swipe, and a value in a URL, and all three want a
 * single word. The sound types are the manifest's own, so a pack that grows a
 * fourth voice grows a fourth facet with no change here.
 */
export type Facet = 'photo' | SoundType;

/**
 * Display order, and the order the vertical swipe steps through.
 *
 * Photo first because it is the facet that always has something to show — a
 * pack without photographs is not a pack — and because "which of these two am
 * I looking at" is the question a learner arrives with. Song before call
 * before alarm matches the species page's own SOUND_ORDER, for the same
 * reason: a bird's song is what you want to hear first.
 */
export const FACETS: readonly Facet[] = ['photo', 'song', 'call', 'alarm'] as const;

export const FACET_LABEL = Object.fromEntries(
  FACETS.map((f) => [f, t(`species.facet.${f}`)]),
) as Record<Facet, string>;

/** Whether a string off the URL or a caller is a facet this app knows. */
export function isFacet(value: string | undefined): value is Facet {
  return !!value && (FACETS as readonly string[]).includes(value);
}

/**
 * How many things a bird has to show on a facet.
 *
 * The count, not a boolean, because the pane shows exactly ONE asset at a time
 * and offers a stepper for the rest — see the note on stepping in
 * `src/app/components/compare.ts`. A missing species counts zero, which is the
 * honest answer for a code from a pack that is no longer selected.
 */
export function facetCount(sp: Species | undefined, facet: Facet): number {
  if (!sp) return 0;
  if (facet === 'photo') return sp.photos.length;
  return sp.audio.filter((a) => a.type === facet).length;
}

/**
 * The facets worth offering for a pair: any that at least ONE of the two can
 * show.
 *
 * Either, not both. "This one has an alarm call recorded and that one doesn't"
 * is a fact about the pair, and hiding the tab would present the gap as though
 * neither bird had ever been recorded scolding. The pane says which of the two
 * is empty, which is the whole difference.
 *
 * Never returns an empty list: two birds with nothing between them still have
 * to be on some screen, and Photo is the one that says "no photo" on both
 * sides rather than leaving a bar with no tabs on it.
 */
export function compareFacets(a: Species | undefined, b: Species | undefined): Facet[] {
  const offered = FACETS.filter((f) => facetCount(a, f) > 0 || facetCount(b, f) > 0);
  return offered.length ? offered : ['photo'];
}

/**
 * Which facet to open on.
 *
 * `prefer` is the caller's own knowledge of why this comparison was opened —
 * a Sound ID round hands over the sound type it just played, because the
 * learner's question is about that clip and not about plumage. It only wins if
 * the pair can actually show it.
 *
 * Failing that: the first facet BOTH birds can show, because a facet only one
 * of them has is a screen with an empty half, and opening on one would make a
 * comparison view's first impression be an absence.
 */
export function defaultFacet(
  a: Species | undefined,
  b: Species | undefined,
  prefer?: Facet,
): Facet {
  const offered = compareFacets(a, b);
  if (prefer && offered.includes(prefer)) return prefer;
  return offered.find((f) => facetCount(a, f) > 0 && facetCount(b, f) > 0) ?? offered[0];
}

/** One labelled block of the "compare to" dropdown. */
export interface CandidateGroup {
  label: string;
  codes: string[];
}

/**
 * Who to offer beside a bird, best first.
 *
 * A flat list of 198 species in alphabetical or even taxonomic order is a
 * scroll, and the bird a learner wants to hold this one against is almost
 * never a random draw from it — it is the bird they have just confused it
 * with, or its nearest relative. So the dropdown leads with the manifest's own
 * confusables (both lists, sound-alikes first, since a comparison view exists
 * mostly for ears), then the family, then the order, then the rest.
 *
 * Nothing is dropped: the last group is everything else in taxonomic order, so
 * the dropdown is still the whole region and the filter box still finds any of
 * it. The groups only decide what you see before you type.
 */
export function compareCandidates(
  anchor: Species,
  all: Species[],
  exclude: string[] = [],
): CandidateGroup[] {
  const skip = new Set([anchor.code, ...exclude]);
  const known = new Map(all.map((sp) => [sp.code, sp]));
  const taken = new Set(skip);

  /** Codes not already spoken for, in the order given, marking them as taken. */
  const claim = (codes: string[]): string[] => {
    const out: string[] = [];
    for (const code of codes) {
      if (taken.has(code) || !known.has(code)) continue;
      taken.add(code);
      out.push(code);
    }
    return out;
  };

  // Sound-alikes before look-alikes, deduped by `claim`. A bird on both lists
  // is the most useful comparison this view can offer, and it appears once.
  const confused = claim([
    ...confusablesFor(anchor, 'sound'),
    ...confusablesFor(anchor, 'names'),
  ]);
  const family = claim(
    anchor.family ? all.filter((sp) => sp.family === anchor.family).map((sp) => sp.code) : [],
  );
  const order = claim(
    anchor.order ? all.filter((sp) => sp.order === anchor.order).map((sp) => sp.code) : [],
  );
  const rest = claim(all.map((sp) => sp.code));

  const groups: CandidateGroup[] = [
    { label: t('species.compareGroup.confused'), codes: confused },
    // Named rather than "Same family": the point of the group is that these are
    // the wrens, and a learner reading "Troglodytidae" has learned something the
    // word "family" cannot tell them.
    { label: anchor.family ?? t('species.compareGroup.family'), codes: family },
    { label: anchor.order ?? t('species.compareGroup.order'), codes: order },
    { label: t('species.compareGroup.rest'), codes: rest },
  ];
  return groups.filter((g) => g.codes.length > 0);
}
